import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import AuthModal from './AuthModal';
import './DesktopPage.css';

/* Installer URLs are env-driven so they can be swapped without a code change. */
const DL = {
  mac: process.env.REACT_APP_DOWNLOAD_MAC || '/downloads/Terview.dmg',
  win: process.env.REACT_APP_DOWNLOAD_WIN || '/downloads/Terview-Setup.exe',
};

const TRUSTED = ['google.com', 'amazon.com', 'microsoft.com', 'meta.com', 'netflix.com', 'stripe.com'];

const STEALTH = [
  { icon: '🖥️', title: 'Invisible on screen share', body: 'The Terview overlay is excluded from screen capture. On Zoom, Teams, Google Meet and Webex, the interviewer sees your screen — never the copilot.' },
  { icon: '🎥', title: 'Hidden from screen recording', body: 'Recordings and proctoring captures come back clean. What you see on your display is not what a capture picks up.' },
  { icon: '📌', title: 'No dock or taskbar icon', body: 'Terview runs without a visible app icon in the macOS Dock or the Windows taskbar — nothing to spot at a glance.' },
  { icon: '🕵️', title: 'Low profile in the process list', body: 'A neutral process name keeps it from standing out in Activity Monitor or Task Manager during a session.' },
  { icon: '⌨️', title: 'Global hotkeys', body: 'Drive everything — show/hide, next answer, capture screen — from OS-level shortcuts. No clicking around, no window switching.' },
  { icon: '🔕', title: 'Silent & window-less', body: 'No notifications, no browser tab, no telltale window in your Alt-Tab / Mission Control switcher.' },
];

const ASSIST = [
  { icon: '🎙️', title: 'Real-time transcription', body: 'Captures the interviewer’s audio the moment they speak and turns it into text on the fly — no copy-paste, no lag.' },
  { icon: '⚡', title: 'Instant structured answers', body: 'STAR-formatted, role-aware answers appear the instant a question lands — tuned to your resume and the job description.' },
  { icon: '💻', title: 'Coding copilot', body: 'Live help on technical screens: working solutions, complexity notes, and edge cases for HackerRank, CoderPad and take-homes.' },
  { icon: '🔁', title: 'Follow-up aware', body: 'Keeps recent context so follow-up questions get coherent answers that build on what was already discussed.' },
  { icon: '📸', title: 'Screen-aware answers', body: 'Capture a region of your screen (a diagram, a code snippet, a question) and get an answer grounded in exactly what’s shown.' },
  { icon: '🌍', title: '50+ languages', body: 'Interview in your language. Terview understands and responds across 50+ languages and accents.' },
];

const OVERLAY = [
  { title: 'Click-through mode', body: 'Let clicks pass straight through the overlay to the app behind it — read your answers without ever losing focus on the meeting.' },
  { title: 'Adjustable opacity', body: 'Dial the overlay from bold to barely-there so it blends into your screen exactly how you like.' },
  { title: 'Move it anywhere', body: 'Drag the panel to any corner or monitor. Multi-display friendly.' },
  { title: 'Smart screen capture', body: 'Draw a box around any part of your screen to send just that region to the AI.' },
];

const PLATFORMS = ['Zoom', 'Microsoft Teams', 'Google Meet', 'Webex', 'HackerRank', 'CoderPad', 'Phone screens', 'On-site (laptop)'];

const STEPS = [
  { n: '1', title: 'Download & install', body: 'Grab the Mac or Windows build and install in under a minute. Sign in with your Terview account.' },
  { n: '2', title: 'Add your resume & the job', body: 'Paste the job description and pick your resume so every answer is tailored to you and the role.' },
  { n: '3', title: 'Start your interview', body: 'Open your meeting as normal. Terview listens, transcribes, and feeds you answers in real time — invisibly.' },
  { n: '4', title: 'Glance & speak', body: 'Read the structured answer, make it your own, and deliver it with confidence. Hide it with a hotkey anytime.' },
];

const COMPARE = [
  ['Excluded from screen share', true, false],
  ['Global OS-level hotkeys', true, false],
  ['No browser tab to hide', true, false],
  ['Adjustable overlay opacity', true, false],
  ['Click-through overlay', true, false],
  ['Screen-region capture', true, false],
  ['Works on on-site / phone screens', true, false],
];

const FAQ = [
  { q: 'Is Terview Desktop really invisible during screen sharing?', a: 'Yes. The overlay is excluded from screen capture, so on Zoom, Teams, Meet and Webex the interviewer sees your screen but not the copilot. It’s also kept out of screen recordings.' },
  { q: 'Which platforms does it work with?', a: 'Any meeting or coding tool — Zoom, Microsoft Teams, Google Meet, Webex, HackerRank, CoderPad, and even phone screens or on-site laptop interviews. It listens to system audio, so it’s not tied to one app.' },
  { q: 'Mac and Windows?', a: 'Both. Native builds for macOS and Windows, with the same real-time assist and stealth features.' },
  { q: 'Does it need my microphone?', a: 'It captures the interviewer’s audio so it can transcribe their questions. Your setup guide walks you through enabling audio capture in a couple of clicks.' },
  { q: 'Will it slow down my computer?', a: 'No — it’s a lightweight native app that stays out of the way. Transcription and answers stream in real time without hogging resources.' },
  { q: 'Is my data private?', a: 'Your resume and session data are yours. Nothing is shown to anyone else, and the overlay never appears in what you share or record.' },
];

function BrandLogo({ domain }) {
  return (
    <div className="dtp-trust-logo">
      <img
        src={`https://logo.clearbit.com/${domain}`}
        data-fb={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`}
        alt={domain}
        loading="lazy"
        onError={e => {
          const fb = e.currentTarget.getAttribute('data-fb');
          if (fb) { e.currentTarget.removeAttribute('data-fb'); e.currentTarget.src = fb; }
          else { const w = e.currentTarget.closest('.dtp-trust-logo'); if (w) w.style.display = 'none'; }
        }}
      />
    </div>
  );
}

export default function DesktopPage() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  return (
    <div className="nh-page dtp-page">
      <Header setShowAuthModal={setShowAuthModal} />

      <main className="dtp-main">
        {/* ── Hero ── */}
        <section className="dtp-hero">
          <span className="dtp-eyebrow"><span className="dtp-dot" /> Terview Desktop</span>
          <h1 className="dtp-h1">Your invisible interview copilot,<br /><span className="dtp-grad">right on your desktop.</span></h1>
          <p className="dtp-lead">
            A native macOS &amp; Windows app that listens to your interview in real time and feeds you exactly what to say —
            invisible on screen share, driven by hotkeys, and always tailored to your resume. Walk in nervous. Walk out hired.
          </p>
          <div className="dtp-hero-cta">
            <a className="dtp-btn dtp-btn-primary" href={DL.mac}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.365 1.43c0 1.14-.42 2.2-1.12 3-.76.88-2 1.56-3.03 1.48-.13-1.1.44-2.27 1.1-3.02.74-.86 2.06-1.5 3.05-1.46zM20.5 17.2c-.55 1.27-.82 1.84-1.53 2.97-.99 1.57-2.38 3.52-4.1 3.53-1.53.02-1.92-1-3.99-.99-2.07.01-2.5 1.01-4.03.99-1.72-.02-3.04-1.78-4.03-3.35-2.77-4.39-3.06-9.54-1.35-12.28 1.21-1.95 3.12-3.09 4.92-3.09 1.83 0 2.98 1.01 4.49 1.01 1.47 0 2.36-1.01 4.48-1.01 1.6 0 3.3.87 4.51 2.38-3.96 2.17-3.32 7.82.16 9.83z"/></svg>
              Download for Mac
            </a>
            <a className="dtp-btn dtp-btn-primary" href={DL.win}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 5.1 10.4 4v7.3H3zM10.4 12.7V20L3 18.9v-6.2zM11.6 3.8 21 2.5v8.8h-9.4zM21 12.7v8.8l-9.4-1.3v-7.5z"/></svg>
              Download for Windows
            </a>
          </div>
          <p className="dtp-hero-note">Free to start · No credit card · macOS 12+ / Windows 10+</p>

          <div className="dtp-trust">
            <p className="dtp-trust-title">Trusted by professionals interviewing at</p>
            <div className="dtp-trust-row">
              {TRUSTED.map(d => <BrandLogo key={d} domain={d} />)}
            </div>
          </div>
        </section>

        {/* ── Stealth / Undetectability ── */}
        <section className="dtp-section">
          <div className="dtp-section-head">
            <span className="dtp-kicker">Undetectable by design</span>
            <h2 className="dtp-h2">Built to stay invisible</h2>
            <p className="dtp-sub">Every layer of the app is engineered to stay out of sight during a live interview — so all the interviewer ever sees is a calm, prepared candidate.</p>
          </div>
          <div className="dtp-grid dtp-grid-3">
            {STEALTH.map(f => (
              <div className="dtp-card" key={f.title}>
                <div className="dtp-card-icon">{f.icon}</div>
                <h3 className="dtp-card-title">{f.title}</h3>
                <p className="dtp-card-body">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Real-time assist ── */}
        <section className="dtp-section dtp-section-alt">
          <div className="dtp-section-head">
            <span className="dtp-kicker">Real-time assist</span>
            <h2 className="dtp-h2">Answers the instant a question lands</h2>
            <p className="dtp-sub">Terview hears the question, understands it in context, and streams a structured, resume-aware answer before you’ve finished processing it yourself.</p>
          </div>
          <div className="dtp-grid dtp-grid-3">
            {ASSIST.map(f => (
              <div className="dtp-card" key={f.title}>
                <div className="dtp-card-icon">{f.icon}</div>
                <h3 className="dtp-card-title">{f.title}</h3>
                <p className="dtp-card-body">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Overlay controls ── */}
        <section className="dtp-section">
          <div className="dtp-section-head">
            <span className="dtp-kicker">You’re in control</span>
            <h2 className="dtp-h2">An overlay that bends to you</h2>
            <p className="dtp-sub">Position it, fade it, click through it. The copilot sits exactly where you want it and never gets in your way.</p>
          </div>
          <div className="dtp-grid dtp-grid-2">
            {OVERLAY.map(f => (
              <div className="dtp-card dtp-card-wide" key={f.title}>
                <h3 className="dtp-card-title">{f.title}</h3>
                <p className="dtp-card-body">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Works everywhere ── */}
        <section className="dtp-section dtp-section-alt">
          <div className="dtp-section-head">
            <span className="dtp-kicker">Works everywhere</span>
            <h2 className="dtp-h2">Any meeting. Any coding tool. Any screen.</h2>
            <p className="dtp-sub">Because it listens to system audio, Terview isn’t tied to one app — it works with whatever your interviewer sends you.</p>
          </div>
          <div className="dtp-chips">
            {PLATFORMS.map(p => <span className="dtp-chip" key={p}>{p}</span>)}
          </div>
        </section>

        {/* ── How it works ── */}
        <section className="dtp-section">
          <div className="dtp-section-head">
            <span className="dtp-kicker">How it works</span>
            <h2 className="dtp-h2">Set up once. Ready in a minute.</h2>
          </div>
          <div className="dtp-steps">
            {STEPS.map(s => (
              <div className="dtp-step" key={s.n}>
                <div className="dtp-step-n">{s.n}</div>
                <div>
                  <h3 className="dtp-card-title">{s.title}</h3>
                  <p className="dtp-card-body">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Comparison ── */}
        <section className="dtp-section dtp-section-alt">
          <div className="dtp-section-head">
            <span className="dtp-kicker">Desktop vs. a browser tab</span>
            <h2 className="dtp-h2">Why the desktop app wins</h2>
          </div>
          <div className="dtp-compare">
            <div className="dtp-compare-row dtp-compare-head">
              <span>Capability</span>
              <span>Terview Desktop</span>
              <span>Browser tab</span>
            </div>
            {COMPARE.map(([label, a, b]) => (
              <div className="dtp-compare-row" key={label}>
                <span>{label}</span>
                <span className={a ? 'dtp-yes' : 'dtp-no'}>{a ? '✓' : '—'}</span>
                <span className={b ? 'dtp-yes' : 'dtp-no'}>{b ? '✓' : '—'}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── System requirements ── */}
        <section className="dtp-section">
          <div className="dtp-section-head">
            <span className="dtp-kicker">System requirements</span>
            <h2 className="dtp-h2">Runs light on your machine</h2>
          </div>
          <div className="dtp-grid dtp-grid-2">
            <div className="dtp-card dtp-card-wide">
              <h3 className="dtp-card-title">macOS</h3>
              <p className="dtp-card-body">macOS 12 (Monterey) or newer · Apple Silicon &amp; Intel · ~200 MB disk · microphone / audio-capture permission.</p>
              <a className="dtp-btn dtp-btn-ghost dtp-btn-sm" href={DL.mac}>Download .dmg</a>
            </div>
            <div className="dtp-card dtp-card-wide">
              <h3 className="dtp-card-title">Windows</h3>
              <p className="dtp-card-body">Windows 10 or 11 (64-bit) · ~200 MB disk · audio-capture setup included in the installer.</p>
              <a className="dtp-btn dtp-btn-ghost dtp-btn-sm" href={DL.win}>Download .exe</a>
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="dtp-section dtp-section-alt">
          <div className="dtp-section-head">
            <span className="dtp-kicker">FAQ</span>
            <h2 className="dtp-h2">Questions, answered</h2>
          </div>
          <div className="dtp-faq">
            {FAQ.map(item => (
              <details className="dtp-faq-item" key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ── Final CTA ── */}
        <section className="dtp-final">
          <h2 className="dtp-h2">Get the copilot on your desktop</h2>
          <p className="dtp-sub">Install in a minute. Invisible on screen share. Tailored to every interview.</p>
          <div className="dtp-hero-cta">
            <a className="dtp-btn dtp-btn-primary" href={DL.mac}>Download for Mac</a>
            <a className="dtp-btn dtp-btn-primary" href={DL.win}>Download for Windows</a>
          </div>
          <p className="dtp-hero-note">Prefer the browser version? <Link to="/InterviewAssistant" className="dtp-inline-link">Try Interview Assistant online →</Link></p>
        </section>
      </main>

      <Footer />
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}
